import React, { ReactNode, FC, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Typography,
} from '@dxb-web-ui-framework/design-system';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { ErrorAlert } from '../ErrorAlert/ErrorAlert';
import { ECOMMBXLoader } from '../ECOMMBXLoader/ECOMMBXLoader';
import { DialogAction } from '../types';
import { GenericDrawer } from '../GenericDrawer';

export type GenericDialogProps = {
  dialogTitle: string;
  dialogContent: ReactNode;
  error?: string;
  isError: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  resetCallback?: () => void;
  dialogActions?: DialogAction[]; // TODO: add correct type
  fullwidth?: boolean;
  isDialogOpen: boolean;
  onToggleDialog: () => void;
  classes?: string;
  dialogContentClassName?: string;
  dialogActionsWrapperClassName?: string;
  dialogTitleClassName?: string;
  disableMobileDrawerHeight?: boolean;
  stickDrawerTitleToLeft?: boolean;
  isConfirmationResponsiveDialog?: boolean;
  disableTitleDrawerPaddings?: boolean;
  genericDrawerTitleTypographyVariant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'caption'
    | 'button'
    | 'overline';
  loaderVerticalSpacing?: 'small' | 'large' | 'medium';
  loaderHorizontalSpacing?: 'small' | 'large' | 'medium';
  disablePortal?: boolean;
  noBottomSpacing?: boolean
};

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    display: 'flex',
  },
  dialog: {
    width: spacing(59),
  },
  content: {
    paddingBottom: (props: any) => (props.isLoading || props.noBottomSpacing ? spacing(0) : spacing(9)),
  },
  dialogActionsSpacing: {
    '& > :not(:first-child)': {
      marginLeft: spacing(2),
    },
  },
}));

// TODO: refactor this to be able to take in a form builder
const GenericDialog: FC<GenericDialogProps> = ({
  isLoading,
  isError,
  error,
  isSuccess,
  isDialogOpen,
  onToggleDialog,
  resetCallback,
  dialogTitle,
  dialogContent,
  dialogActions,
  fullwidth,
  classes: dialogClasses,
  dialogContentClassName,
  dialogTitleClassName,
  disableMobileDrawerHeight,
  dialogActionsWrapperClassName,
  genericDrawerTitleTypographyVariant,
  stickDrawerTitleToLeft,
  isConfirmationResponsiveDialog,
  disableTitleDrawerPaddings,
  loaderVerticalSpacing = 'large',
  loaderHorizontalSpacing,
  disablePortal,
  noBottomSpacing
}) => {
  const classes = useStyles({ isLoading, noBottomSpacing });
  const { isExtraSmallScreen: isMobile } = useScreenDetector();

  const muiCloseAnimationDuration = 195; // in ms

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isSuccess) {
      onToggleDialog();
      timer = setTimeout(() => {
        resetCallback && resetCallback();
      }, muiCloseAnimationDuration);
    }
    return () => clearTimeout(timer);
  }, [isSuccess, onToggleDialog, resetCallback]);

  const onToggleDrawer = useCallback(() => {
    let timer: NodeJS.Timeout;

    onToggleDialog();
    timer = setTimeout(() => {
      resetCallback && resetCallback();
    }, muiCloseAnimationDuration);

    return () => clearTimeout(timer);
  }, [onToggleDialog, resetCallback]);

  if (isMobile) {
    return (
      <GenericDrawer
        isOpen={isDialogOpen}
        title={dialogTitle}
        disableMobileDrawerHeight={disableMobileDrawerHeight}
        anchor={'bottom'}
        onToggleDrawer={onToggleDrawer}
        stickDrawerTitleToLeft={stickDrawerTitleToLeft}
        titleTypographyVariant={genericDrawerTitleTypographyVariant}
        isConfirmationResponsiveDialog={isConfirmationResponsiveDialog}
        disableTitleDrawerPaddings={disableTitleDrawerPaddings}>
        <Box height='100%' pt={2} pb={3} px={3} display='flex' flexDirection='column'>
          <Grid container alignItems='center' justifyContent='center' spacing={isConfirmationResponsiveDialog ? 0 : 3}>
            <Grid item xs={12}>
              {isError && <ErrorAlert message={error} hasSpacing />}
              <Box
                pb={isConfirmationResponsiveDialog && !isLoading ? 5 : 0}
                pt={isConfirmationResponsiveDialog && error ? 2 : 0}>
                {dialogContent}
              </Box>
              {isLoading && (
                <ECOMMBXLoader
                  size={'small'}
                  verticalSpacing={loaderVerticalSpacing}
                  horizontalSpacing={loaderHorizontalSpacing}
                />
              )}
            </Grid>
          </Grid>
          {dialogActions && (
            <Box display='flex' flexDirection='column' flexGrow={1} justifyContent='flex-end'>
              {dialogActions.map((action, index) => (
                <Box key={`dialog-action-${action.label}`} py={dialogActions.length === 1 ? 0 : 0.5}>
                  <Button
                    fullWidth
                    key={index}
                    size='large'
                    color={action.color}
                    variant={action.variant}
                    onClick={action.onClick}
                    title={action.label}
                    disabled={action?.disabled ? action.disabled : false}>
                    <Typography variant='body1'>{action.label}</Typography>
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </GenericDrawer>
    );
  }
  return (
    <Dialog
      data-testid='dialog'
      disablePortal={disablePortal}
      open={isDialogOpen}
      onClose={onToggleDrawer}
      classes={{
        paper: dialogClasses ?? classes.dialog,
      }}>
      {isError && <ErrorAlert message={error} />}
      <DialogTitle className={dialogTitleClassName}>{dialogTitle}</DialogTitle>
      <DialogContent className={dialogContentClassName ?? classes.content}>{dialogContent}</DialogContent>
      {isLoading && (
        <ECOMMBXLoader
          size={'small'}
          verticalSpacing={loaderVerticalSpacing}
          horizontalSpacing={loaderHorizontalSpacing}
        />
      )}
      {dialogActions && (
        <DialogActions
          classes={{ root: dialogActionsWrapperClassName ?? classes.root, spacing: classes.dialogActionsSpacing }}>
          {dialogActions.map((action, index) => (
            <Box>
              <Button
                fullWidth={fullwidth}
                key={index}
                color={action.color}
                size='large'
                variant={action.variant}
                onClick={action.onClick}
                title={action.label}
                disabled={action?.disabled ? action.disabled : false}>
                {action.label}
              </Button>
            </Box>
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

export { GenericDialog };
